import React, { FC, MouseEventHandler, useState } from 'react';
import styles from './uploadModal.module.scss';
import OrganizationModal from 'shared/components/OrganizationModal';
import { Col, Row, Spin, UploadProps } from 'antd';
import UploadComponent from 'shared/components/UploadComponent';
import UploadIcon from 'assets/icons/upload_icon.svg';
import ErrorFile from 'assets/icons/file_icon_red.svg';
import SuccessFile from 'assets/icons/file_icon_green.svg';
import RemoveIcon from 'assets/icons/remove_icon.svg';
import clsx from 'clsx';
import COButton from 'shared/components/COButton';
import { ButtonSize } from 'enums/ButtonEnums';
import { RcFile } from 'antd/lib/upload';
import {
  UploadModalConstants,
  SCOPE_TITLES,
} from 'constants/uploadModal.constants';
import ActivityDataEntryService from 'services/ActivityDataEntryService';
import { queryParamsList } from '..';
import useFilters from 'shared/hooks/useFilters';
import { useParams } from 'react-router-dom';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { DownloadLoader } from 'enums/GraphDropdownEnums';
import { downloadBlob } from 'shared/utils/handleBlobDownload';
import { ProcessingFacilityTableProps } from 'enums/ProcessingFacilityEnums';
import { category5, scope3 } from 'constants/constants';

const {
  DEFAULT_ERROR_FILENAME,
  UPLOAD_ERROR_ALT,
  UPLOAD_SUCCESS_ALT,
  UPLOAD_ALT,
  GO_BACK_BUTTON,
  PROCEED_BUTTON,
  REMOVE_ALT,
  INVALID_FILE_FORMAT,
  DOWNLOAD_ERROR_FILE,
  DRAG_OR_CLICK,
  FILE_INFO,
  UPLOAD_COMPONENT_NAME,
  PERMITTED_FILES,
} = UploadModalConstants;

interface UploadModalProps {
  visible: boolean;
  onClose: () => void;
  activeScope: string;
  successCallback?: () => Promise<void>;
  category?: string; // Category identifier (category5, category6, etc.)
}

const UploadModal: FC<UploadModalProps> = ({
  visible,
  onClose,
  activeScope,
  successCallback,
  category,
}) => {
  const { id: locationId } = useParams();
  const { getParams } = useFilters();
  const { activeTab } = getParams(queryParamsList);
  const { uploadScopeTemplate, loading, errorFile, isSuccessfulUpload } =
    ActivityDataEntryService();
  const [file, setFile] = useState<RcFile>();
  const handleUpload: UploadProps['customRequest'] = async options => {
    if (!locationId) return;
    const file = options.file as RcFile;
    setFile(file);
    // Use provided category, or fallback to category5 for scope3 (backward compatibility)
    const categoryToUse = category || (activeScope === scope3 ? category5 : undefined);
    await uploadScopeTemplate(
      locationId,
      activeTab,
      activeScope,
      file,
      categoryToUse
    );
  };
  const removeFile: MouseEventHandler = e => {
    e.stopPropagation();
    setFile(undefined);
  };
  const handleClose = () => {
    setFile(undefined);
    onClose();
  };

  const handleDownloadErrorFile: MouseEventHandler = e => {
    e.stopPropagation();
    if (errorFile) {
      downloadBlob(errorFile, DEFAULT_ERROR_FILENAME);
    }
  };

  const uploadContent = (
    <Spin spinning={loading} tip={DownloadLoader.UPLOADING}>
      <Row
        className={clsx(styles.uploadChildrenContainer, {
          [styles.success]: file && isSuccessfulUpload,
          [styles.error]: file && !isSuccessfulUpload,
        })}>
        {file?.name ? (
          <>
            <Col>
              <img
                src={isSuccessfulUpload ? SuccessFile : ErrorFile}
                alt={isSuccessfulUpload ? UPLOAD_SUCCESS_ALT : UPLOAD_ERROR_ALT}
                className={styles.uploadIcon}
              />
            </Col>
            <Col>
              <Row align="middle">
                <Col>{file.name}</Col>
                {(!isSuccessfulUpload && (
                  <Col className={styles.remove} onClick={removeFile}>
                    <img
                      src={RemoveIcon}
                      alt={REMOVE_ALT}
                      className={styles.remove}
                    />
                  </Col>
                )) ??
                  ''}
              </Row>
            </Col>
            {errorFile ? (
              <Col span={24} className={styles.errorFile}>
                <Row justify="center">
                  <Col>
                    <ExclamationCircleOutlined />
                    {INVALID_FILE_FORMAT}
                  </Col>
                  <Col
                    className={styles.errorFileDownload}
                    onClick={handleDownloadErrorFile}>
                    {DOWNLOAD_ERROR_FILE}
                  </Col>
                </Row>
              </Col>
            ) : null}
          </>
        ) : (
          <>
            <Col>
              <img
                src={UploadIcon}
                alt={UPLOAD_ALT}
                className={styles.uploadIcon}
              />
            </Col>
            <Col>{DRAG_OR_CLICK}</Col>
          </>
        )}
      </Row>
    </Spin>
  );

  const activeScopeTitle =
    activeScope == ProcessingFacilityTableProps.SCOPE1_INDEX
      ? SCOPE_TITLES.SCOPE_1
      : activeScope == ProcessingFacilityTableProps.SCOPE2_INDEX
      ? SCOPE_TITLES.SCOPE_2
      : SCOPE_TITLES.SCOPE_3;
  return (
    <div>
      <OrganizationModal
        visible={visible}
        onClose={handleClose}
        children={
          <Row className={styles.uploadModalContainer}>
            <Col span={24} className={styles.uploadTitle}>
              {`Upload ${activeScopeTitle} template`}
            </Col>
            <Col span={24} className={styles.uploadFilesInfo}>
              {FILE_INFO}
            </Col>
            <Col span={24} className={styles.uploader}>
              <UploadComponent
                name={UPLOAD_COMPONENT_NAME}
                accept={PERMITTED_FILES}
                customRequest={handleUpload}
                maxCount={1}
                fileList={file ? [file] : []}
                showUploadList={false}
                children={uploadContent}
              />
            </Col>
            <Col span={24}>
              <Row className={styles.buttonsContainer} justify="center">
                <Col span={10}>
                  <COButton
                    primary
                    size={ButtonSize.MEDIUM}
                    loading={loading}
                    disabled={!isSuccessfulUpload}
                    onClick={successCallback}>
                    {PROCEED_BUTTON}
                  </COButton>
                </Col>
                <Col span={10}>
                  <COButton
                    secondary
                    size={ButtonSize.MEDIUM}
                    disabled={isSuccessfulUpload}
                    onClick={handleClose}>
                    {GO_BACK_BUTTON}
                  </COButton>
                </Col>
              </Row>
            </Col>
          </Row>
        }
      />
    </div>
  );
};

export default UploadModal;
